import { Stack, Typography } from '@mui/material'
import dynamic from 'next/dynamic'

import { ConsoleBusinessEditQuery, Maybe } from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { handleChunkLoadError } from '@/lib/errorHandler'
import { AddressInfo } from '@/lib/zipCloud'

export * from './fragments'

export type State = {
  artistData: Maybe<ConsoleBusinessEditQuery>
  artistId: string
}

export type Action = {
  onSubmit: (inputs: {
    birthday: string | null
    state: string
    stateKana: string
    city: string
    cityKana: string
    line1: string
    line1Kana: string
    line2: string
    line2Kana: string
    postalCode: string
    firstNameKana: string
    firstNameKanji: string
    lastNameKana: string
    lastNameKanji: string
    gender: string
    phone: string
    town: string
    townKana: string
  }) => Promise<void>
  onGetAddressInfoByCode: (code: string) => Promise<AddressInfo[]>
}

export const ConsoleBusinessEditTemplate: Template<State, Action> = ({
  state,
  onSubmit,
  onGetAddressInfoByCode,
}) => {
  const { t } = useTranslation('consoleBusinessEdit')
  const { routerNames, routerPages } = useRouter()
  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        margin
        header={{
          title: t('businessInformationSettings'),
          back: true,
          defaultBackPage: routerPages.ConsoleAccountSetting,
        }}
      >
        <Head title={routerNames.ConsoleBusinessEdit} />
        <Stack sx={{ pt: 5, pb: 3 }}>
          <Stack spacing={5} sx={{ pb: 8 }}>
            <Typography variant="h5">
              {t('registeredContactInformation')}
            </Typography>
            <Typography variant="body2">
              {state.artistData?.artist?.business
                ? [
                    `${state.artistData?.artist?.business.lastNameKanji}${state.artistData?.artist?.business.firstNameKanji}`,
                    t(`${state.artistData?.artist?.business.gender}`),
                    state.artistData?.artist?.business.formattedBirthday,
                    state.artistData?.artist?.business.phone,
                  ].join('\n')
                : t('Unregistered')}
            </Typography>
          </Stack>
          <Stack spacing={5} sx={{ pb: 8 }}>
            <Typography variant="h5">{t('registeredAddress')}</Typography>
            <Typography variant="body2">
              {state.artistData?.artist?.business ? (
                <>
                  {state.artistData?.artist?.business.addressKanji?.postalCode.replace(
                    /[Ａ-Ｚａ-ｚ０-９]/g,
                    s => String.fromCharCode(s.charCodeAt(0) - 65248)
                  )}
                  <br />
                  {state.artistData?.artist?.business.addressKanji?.state}
                  {state.artistData?.artist?.business.addressKanji?.city}
                  {state.artistData?.artist?.business.addressKanji?.town}
                  {state.artistData?.artist?.business.addressKanji?.line1}
                  {state.artistData?.artist?.business.addressKanji?.line2}
                </>
              ) : (
                t('Unregistered')
              )}
            </Typography>
          </Stack>
          <ConsoleBusinessEditForm
            onSubmit={onSubmit}
            onGetAddressInfoByCode={onGetAddressInfoByCode}
          />
        </Stack>
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const ConsoleBusinessEditForm = dynamic<{
  onSubmit: Action['onSubmit']
  onGetAddressInfoByCode: Action['onGetAddressInfoByCode']
}>(
  import('./ConsoleBusinessEditForm')
    .then(m => m.ConsoleBusinessEditForm)
    .catch(async error => {
      await handleChunkLoadError(error)
      return () => null
    }),
  { ssr: false }
)
